import { useState, useCallback } from 'react'
import { Modal, FormField, btnPrimary, btnSecondary, colors } from '../../components/ui'
import { SearchSelect } from '../../components/ui/SearchSelect'
import { api, ApiError } from '../../services/api'
import type { Patient } from '@alpha-edge/shared'

interface ExternalResultModalProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly onCreated: () => void
}

const resultValueOptions = [
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
  { value: 'invalid', label: 'Invalid' },
  { value: 'indeterminate', label: 'Indeterminate' },
]

export function ExternalResultModal({ isOpen, onClose, onCreated }: ExternalResultModalProps) {
  const [patientId, setPatientId] = useState('')
  const [testType, setTestType] = useState('')
  const [resultValue, setResultValue] = useState('')
  const [externalLabName, setExternalLabName] = useState('')
  const [externalAccessionId, setExternalAccessionId] = useState('')
  const [resultDate, setResultDate] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isValid = patientId && testType && resultValue && externalLabName

  const [patientDisplay, setPatientDisplay] = useState('')

  const searchPatients = useCallback(async (query: string) => {
    const res = await api.get<Patient[]>(`/patients?search=${encodeURIComponent(query)}&limit=10`)
    return res.data ?? []
  }, [])

  async function handleSubmit() {
    if (!isValid) return
    setSubmitting(true)
    setError(null)
    try {
      await api.post('/results/external', {
        patientId,
        testType,
        resultValue,
        externalLabName,
        externalAccessionId: externalAccessionId || undefined,
        resultTimestamp: resultDate ? new Date(resultDate).toISOString() : undefined,
        note: note || undefined,
      })
      onCreated()
      handleClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create external result')
    } finally {
      setSubmitting(false)
    }
  }

  function handleClose() {
    setPatientId('')
    setPatientDisplay('')
    setTestType('')
    setResultValue('')
    setExternalLabName('')
    setExternalAccessionId('')
    setResultDate('')
    setNote('')
    setError(null)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Record External / Send-Out Result" width="520px">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.25rem' }}>
            Patient *
          </label>
          <SearchSelect<Patient>
            placeholder="Search by name or MRN..."
            value={patientId}
            displayValue={patientDisplay}
            onSearch={searchPatients}
            onSelect={(p) => {
              setPatientId(p.id)
              setPatientDisplay(`${p.lastName}, ${p.firstName}`)
            }}
            renderItem={(p) => (
              <span>
                {p.lastName}, {p.firstName} (MRN: {p.medicalRecordNumber ?? 'N/A'})
              </span>
            )}
            keyField="id"
          />
        </div>

        <FormField
          name="testType"
          label="Test Type"
          required
          value={testType}
          onChange={setTestType}
          placeholder="e.g. CBC, Lipid Panel, TSH"
        />

        <FormField name="resultValue" label="Result Value" required>
          <select
            id="resultValue"
            value={resultValue}
            onChange={(e) => setResultValue(e.target.value)}
            style={{ ...selectStyle }}
          >
            <option value="">Select result...</option>
            {resultValueOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </FormField>

        <FormField
          name="externalLabName"
          label="External Lab Name"
          required
          value={externalLabName}
          onChange={setExternalLabName}
          placeholder="e.g. Quest Diagnostics, LabCorp"
        />

        <FormField
          name="externalAccessionId"
          label="External Accession / Reference #"
          value={externalAccessionId}
          onChange={setExternalAccessionId}
          placeholder="Reference number from external lab"
        />

        <FormField name="resultDate" label="Result Date" type="date" value={resultDate} onChange={setResultDate} />

        <FormField
          name="note"
          label="Note"
          multiline
          value={note}
          onChange={setNote}
          placeholder="Additional notes about this external result..."
        />

        {error && <div style={{ color: colors.danger, fontSize: '0.875rem', padding: '0.5rem 0' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '0.5rem' }}>
          <button type="button" style={btnSecondary} onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            style={{ ...btnPrimary, opacity: isValid && !submitting ? 1 : 0.5 }}
            disabled={!isValid || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Saving...' : 'Record Result'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  borderRadius: '4px',
  border: `1px solid ${colors.border}`,
  fontSize: '0.9375rem',
  backgroundColor: '#fff',
}
